import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HeartIcon } from '@radix-ui/react-icons';
import {
	PageHeader, Button, Badge, Card, CardContent, EmptyState, Alert, AlertTitle, AlertDescription,
	PlatformMark, TableSkeleton,
} from '../components/ui';
import { MoodCell, TakePulseDialog, STATUS_LABEL } from '../components/features/heartbeat';
import { useHeartbeatIndex, useClients, useTakePulseFor } from '../services/queries';
import { EMOTIONS, EMOTION_META } from '../constants/sentiment';
import { ROUTE_PATTERNS, buildRoute } from '../constants/Paths';
import { relativeTime } from '../utils/dates';
import HeartbeatNavigation from '../components/features/heartbeat/HeartbeatNavigation';

const ACTIVE = ['queued', 'collecting', 'labeling'];

const SORTS = [
	{ value: 'recent', label: 'Newest first' },
	{ value: 'oldest', label: 'Oldest first' },
	{ value: 'replies', label: 'Most replies' },
	{ value: 'against', label: 'Most against you' },
];

const STATUS_FILTERS = [
	{ value: '', label: 'Any status' },
	{ value: 'queued,collecting,labeling', label: 'Still running' },
	{ value: 'ready', label: 'Ready' },
	{ value: 'partial', label: 'Partial' },
	{ value: 'failed', label: 'Failed' },
];

const PLATFORMS = [
	{ value: '', label: 'Any platform' },
	{ value: 'x', label: 'X' },
	{ value: 'tiktok', label: 'TikTok' },
	{ value: 'reddit', label: 'Reddit' },
];

/**
 * Every pulse across every account - level 1 of the revised structure.
 *
 * `/heartbeat` used to render the account page with no account, which could
 * only say "Pick an account to listen to". The most useful screen in the
 * feature was an empty prompt.
 */
export default function HeartbeatIndexPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [dialogOpen, setDialogOpen] = useState(false);

	const clientId = searchParams.get('client') || '';
	const platform = searchParams.get('platform') || '';
	const status = searchParams.get('status') || '';
	const emotion = searchParams.get('emotion') || '';
	const sort = searchParams.get('sort') || 'recent';

	// Functional form, not `new URLSearchParams(searchParams)`. Reading the
	// captured `searchParams` means any two writes that happen WITHOUT an
	// intervening render both build from the same value, and the second
	// discards the first. Two ordinary clicks re-render in between and were
	// never affected; what this protects is the case with no render between -
	// two components writing in one tick (FilterBar's chips and a page's sort
	// select both write here), or a write from an effect alongside a user
	// action. Demonstrated by setting two selects in a single tick: the result
	// was "?platform=tiktok" with the sort dropped.
	const setParam = (key, value) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (value) next.set(key, value); else next.delete(key);
			return next;
		}, { replace: true });
	};

	// Bound to the URL so a filtered view is a link someone can send.
	const params = useMemo(() => {
		const p = { limit: 25, sort };
		if (clientId) p.clientId = clientId;
		if (platform) p.platform = platform;
		if (status) p.status = status;
		if (emotion) p.emotion = emotion;
		return p;
	}, [clientId, platform, status, emotion, sort]);

	const { data, isLoading, error } = useHeartbeatIndex(params);
	const { data: clients = [] } = useClients();
	const takePulse = useTakePulseFor();

	const runs = data?.runs || [];
	const filtered = !!(clientId || platform || status || emotion);

	const startPulse = async ({ accountId, requestedPosts }) => {
		await takePulse.mutateAsync({ accountId, requestedPosts });
		setDialogOpen(false);
	};

	const selectCls = 'rounded-lg border border-border bg-card px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600';

	return (
		<main className="p-4 sm:p-8 max-w-7xl mx-auto">
			<PageHeader
				title="Heartbeat"
				description="How the internet feels about the accounts you look after."
				actions={<Button onClick={() => setDialogOpen(true)}>Take a pulse</Button>}
			/>
			<HeartbeatNavigation />

			<div className="mb-4 flex flex-wrap items-center gap-2">
				<select value={clientId} onChange={(e) => setParam('client', e.target.value)} className={selectCls} aria-label="Client">
					<option value="">All clients</option>
					{clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
				</select>
				<select value={platform} onChange={(e) => setParam('platform', e.target.value)} className={selectCls} aria-label="Platform">
					{PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
				</select>
				<select value={status} onChange={(e) => setParam('status', e.target.value)} className={selectCls} aria-label="Status">
					{STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
				</select>
				<select value={emotion} onChange={(e) => setParam('emotion', e.target.value)} className={selectCls} aria-label="Dominant emotion">
					<option value="">Any feeling</option>
					{EMOTIONS.map((e) => <option key={e} value={e}>{EMOTION_META[e].label}</option>)}
				</select>

				<select value={sort} onChange={(e) => setParam('sort', e.target.value)} className={`${selectCls} ml-auto`} aria-label="Sort by">
					{SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
				</select>

				{filtered && (
					<Button variant="outline" size="sm" onClick={() => setSearchParams(sort === 'recent' ? {} : { sort }, { replace: true })}>
						Clear filters
					</Button>
				)}
			</div>

			{error && (
				<Alert variant="destructive" className="mb-4">
					<AlertTitle>We couldn't load your pulses</AlertTitle>
					<AlertDescription>{error.response?.data?.error || 'Please try again in a moment.'}</AlertDescription>
				</Alert>
			)}

			{isLoading && <TableSkeleton rows={5} />}

			{!isLoading && !error && runs.length === 0 && (
				<Card>
					<CardContent>
						<EmptyState
							icon={HeartIcon}
							title={filtered ? 'No pulses match those filters' : 'No pulses yet'}
							description={filtered
								? 'Try a wider date range, another client, or clear the filters.'
								: "Take the first pulse and watch the feelings come in. It usually takes a few minutes."}
							action={filtered ? null : <Button onClick={() => setDialogOpen(true)}>Take a pulse</Button>}
						/>
					</CardContent>
				</Card>
			)}

			{!isLoading && runs.length > 0 && (
				<Card>
					<CardContent className="p-0">
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
										<th className="px-4 py-2.5 font-medium">Account</th>
										<th className="px-4 py-2.5 font-medium">Client</th>
										<th className="px-4 py-2.5 font-medium">Taken</th>
										<th className="px-4 py-2.5 font-medium">Status</th>
										<th className="px-4 py-2.5 font-medium">Heard</th>
										<th className="px-4 py-2.5 font-medium">Standing</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-border">
									{runs.map((r) => {
										const acc = r.account || {};
										const isActive = ACTIVE.includes(r.status);
										const to = acc._id
											? buildRoute(ROUTE_PATTERNS.HEARTBEAT_RUN, { accountId: acc._id, runId: r._id })
											: null;
										return (
											<tr key={r._id} className="hover:bg-muted">
												<td className="px-4 py-3">
													<div className="flex items-center gap-2.5">
														<div className="relative shrink-0">
															<img
																src={acc.avatar || 'https://placehold.co/32x32'}
																alt=""
																className="h-8 w-8 rounded-full bg-muted object-cover"
															/>
															<PlatformMark platform={r.platform} size="xs" className="absolute -bottom-0.5 -right-0.5 ring-2 ring-white" />
														</div>
														<div className="min-w-0">
															{to ? (
																<Link to={to} className="block truncate font-medium text-foreground hover:text-brand-700 hover:underline">
																	@{acc.handle || 'unknown'}
																</Link>
															) : (
																<span className="block truncate font-medium text-foreground">@{acc.handle || 'unknown'}</span>
															)}
															{acc.displayName && <span className="block truncate text-xs text-muted-foreground">{acc.displayName}</span>}
														</div>
													</div>
												</td>
												<td className="px-4 py-3 text-foreground">{r.client?.name || '-'}</td>
												<td className="px-4 py-3 text-foreground" title={new Date(r.createdAt).toLocaleString()}>
													{relativeTime(r.createdAt) || '-'}
												</td>
												<td className="px-4 py-3">
													<Badge variant="outline" size="sm">{STATUS_LABEL[r.status] || r.status}</Badge>
													{r.status === 'failed' && r.error && (
														<span className="mt-1 block max-w-[16rem] truncate text-xs text-red-700" title={r.error}>{r.error}</span>
													)}
												</td>
												<td className="px-4 py-3 tabular-nums text-foreground">
													{isActive ? (
														<span className="text-muted-foreground">
															{r.replies?.collected || 0} so far
														</span>
													) : (
														<>
															{r.replies?.collected || 0} replies
															<span className="block text-xs text-muted-foreground">{r.targetsCount || 0} posts</span>
														</>
													)}
												</td>
												<td className="px-4 py-3">
													<MoodCell totals={r.totals} status={r.status} />
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			)}

			{data?.truncated && (
				<p className="mt-3 text-sm text-muted-foreground">
					Showing the top {runs.length} for this sort. Narrow the filters, or sort by newest to page through everything.
				</p>
			)}

			<TakePulseDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				onStart={startPulse}
				starting={takePulse.isPending}
				error={takePulse.error ? (takePulse.error.response?.status === 402
					? "This workspace's plan doesn't cover another pulse right now."
					: "We couldn't start this pulse. Please try again in a few minutes.") : null}
			/>
		</main>
	);
}
